import Image, { StaticImageData } from 'next/legacy/image';
import HnaviLogo from '@images/hnavi-logo.png';
import CheckboxIcon from '@images/checkbox.svg';
import WorryingMan from '@images/worrying-man.svg';
import Feature1 from '@images/feature-1.svg';
import Feature2 from '@images/feature-2.svg';
import Feature3 from '@images/feature-3.svg';
import { Triangle, LinkBracket } from '@components';
import { beginnersPath } from '@paths';
import { numbers } from '@constants';

const Circle: React.FC<{
  title: React.ReactElement<any> | string;
  body: string;
}> = ({ title, body }) => (
  <div className='flex h-20 w-20 flex-col items-center justify-center rounded-full bg-white md:h-36 md:w-36'>
    <div className='text-xs leading-none md:text-base'>{title}</div>
    <div
      className='text-top-primary md:text-4xl'
      style={{ fontFamily: 'Roboto Condensed' }}
    >
      {body}
    </div>
  </div>
);

const Section1 = () => (
  <div className='bg-lightgray py-12 text-center font-bold'>
    <div className='flex flex-col items-center justify-center text-primary md:flex-row'>
      <span className='md:mr-4 md:text-3xl'>
        即戦力のシステム開発会社を探すなら
      </span>
      <img
        src={HnaviLogo.src}
        alt='発注ナビロゴ'
        width={HnaviLogo.width}
        height={HnaviLogo.height}
        loading='lazy'
      />
    </div>
    <div className='md:mb-8 md:mt-4 md:text-lg'>
      発注ナビは、システム開発に特化した
      <br className='md:hidden' />
      発注先選定支援サービスです。
    </div>
    <div className='my-4 flex justify-center gap-4'>
      <Circle title='紹介実績' body={numbers.matchCount} />
      <Circle title='対応社数' body={numbers.developerCount} />
      <Circle
        title={
          <>
            対応
            <br />
            テクノロジー
          </>
        }
        body={numbers.technologyCount}
      />
      <Circle title='紹介達成数' body={numbers.matchRate} />
    </div>
  </div>
);

const NayamiItem: React.FC<{ children: any }> = ({ children }) => (
  <div className='mb-4 flex items-start gap-4 text-2xl leading-loose'>
    <Image src={CheckboxIcon} alt='checkbox' />
    <div>{children}</div>
  </div>
);

const NayamiEm: React.FC<{ children: any }> = (props) => (
  <span
    className='text-3xl text-top-primary underline md:text-4xl'
    style={{ textDecorationStyle: 'wavy' }}
    {...props}
  />
);

const NayamiSection = () => {
  return (
    <div className='bg-primary-bright py-12 font-bold md:py-24'>
      <div className='container mx-auto flex flex-col items-center md:flex-row-reverse'>
        <div className='flex shrink-0 flex-col'>
          <div className='rounded-full bg-secondary2 p-3 text-center text-white md:px-12 md:py-6 md:text-2xl'>
            システム開発の発注先探しで
            <br />
            <big>こんなお悩みありませんか？</big>
          </div>
          <Triangle className='mx-auto mb-4 w-6 text-secondary2' />

          <div className='mx-auto'>
            <NayamiItem>
              なかなか<NayamiEm>いい外注業者</NayamiEm>が
              <br className='lg:hidden' />
              見つからない。
            </NayamiItem>
            <NayamiItem>
              <NayamiEm>ITの知識がなくて</NayamiEm>、
              <br className='lg:hidden' />
              発注内容をまとめられない。
            </NayamiItem>
            <NayamiItem>
              忙しくて外注業者を探す
              <br className='lg:hidden' />
              <NayamiEm>時間がない</NayamiEm>。
            </NayamiItem>
          </div>
        </div>

        <div className='mx-auto mt-8 w-2/3 text-center md:w-2/5'>
          <Image src={WorryingMan} alt='悩んでいる人物' />
        </div>
      </div>
    </div>
  );
};

const Feature: React.FC<{
  image: StaticImageData;
  title: string;
  body: string;
}> = ({ image, title, body }) => (
  <div className='relative mt-52 rounded-lg bg-lightgray p-7 pb-9 pt-20'>
    <div className='absolute inset-x-0 bottom-[calc(100%-4rem)]'>
      <Image src={image} alt={title} />
    </div>
    <div className='mb-4 text-2xl font-bold text-primary'>{title}</div>
    <div className='text-left'>{body}</div>
  </div>
);

const Sarani = () => (
  <div className='absolute inset-x-0 bottom-[calc(100%+1rem)] flex flex-col lg:bottom-[calc(100%-3.5rem)] lg:left-auto lg:right-full lg:flex-row'>
    <div className='mx-auto mt-8 flex h-20 w-20 items-center justify-center rounded-full bg-top-primary text-lg text-white lg:mt-0'>
      さらに
    </div>
    <Triangle className='mx-auto -mt-1 w-4 text-top-primary lg:-ml-1 lg:mr-4 lg:mt-0 lg:-rotate-90' />
  </div>
);

const FeaturesSection = () => (
  <div className='container mx-auto py-8 text-center lg:py-16'>
    <div className='my-8 text-2xl font-bold text-secondary2 md:text-3xl'>
      発注ナビの主な特徴
    </div>
    <div className='gap-8 lg:grid lg:grid-cols-3'>
      <Feature
        image={Feature1}
        title='IT案件に特化'
        body={`日本最大級${numbers.developerCount}社以上のシステム開発・WEB制作会社が登録。IT専門だから細かい要望が伝わり、理想的なパートナーが見つかる。`}
      />
      <Feature
        image={Feature2}
        title='ITへの不安を徹底サポート'
        body='専門コンシェルジュがしっかりヒアリングするので、IT知識に不安があっても、まだ要件が固まっていなくても大丈夫。'
      />
      <Feature
        image={Feature3}
        title='完全無料・最短翌日紹介'
        body='コンシェルジュに発注内容を話すだけで最短翌日に開発会社をご紹介。しかも完全無料・成約手数料も無し。'
      />
    </div>
    <div className='mt-20 flex flex-col items-center rounded-lg bg-lightgray p-7 pt-16 lg:pt-12'>
      <div className='relative mb-4 text-2xl font-bold text-primary'>
        <Sarani />
        東証プライム上場
        <br className='md:hidden' />
        「アイティメディア株式会社」
        <br className='md:hidden' />
        グループが運営
      </div>
      <div className='text-center'>
        ご相談内容は一般公開しないため、クローズド案件でも安心。
        <br />
        ご紹介企業は第三者調査機関にて信用情報・事業継続性を確認済です。
      </div>
    </div>
  </div>
);

const BegginerLink = () => (
  <div className='flex bg-top-primary py-10'>
    <a
      href={beginnersPath}
      className='mx-auto flex items-center rounded-full bg-white px-6 py-4 text-2xl font-bold text-top-primary hover:text-primary md:px-12'
    >
      <span className='hidden md:inline'>ご利用の流れなど、</span>
      <span className='mr-4 md:mr-6'>はじめての方はこちら</span>
      <LinkBracket />
    </a>
  </div>
);

export const HnaviFooter = () => {
  return (
    <>
      <style>{`@font-face{font-family:'Roboto Condensed';font-style:normal;font-weight:400;font-display:swap;src:url(https://fonts.gstatic.com/l/font?kit=ieVo2ZhZI2eCN5jzbjEETS9weq8-_d6T_POl0fRJeyWyovBM93N5GO64844416Uo&skey=9986ecffddb755ab&v=v27) format('woff2');`}</style>
      <Section1 />
      <NayamiSection />
      <FeaturesSection />
      <BegginerLink />
    </>
  );
};
